import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { palette } from '@openx/components-core/lib/theme/paletteV2';
import type { DealInsightsBidRateWinRateApi } from '@openx/types/dealInsights';
import { getFormattedAmountValue } from '@openx/utils/lib/getFormattedAmountValue';
import { format, parseISO } from 'date-fns';
import { useTranslation } from 'react-i18next';
import type { NameType, Payload, ValueType } from 'recharts/types/component/DefaultTooltipContent';
import { TooltipMetrics } from '../../shared/TooltipMetrics';

export type SpendTooltipPayload = Omit<Payload<ValueType, NameType>, 'payload'> & {
  payload?: Partial<DealInsightsBidRateWinRateApi>;
}; // removes the original `payload: any` and replaces it with our typed version

type SpendTooltipProps = {
  label?: string;
  payload?: SpendTooltipPayload[];
};

export const SpendTooltip = ({ label, payload }: SpendTooltipProps) => {
  const { t } = useTranslation();

  const spendPoint: SpendTooltipPayload | undefined = payload?.find(point => point.dataKey === 'spend');
  const ecpmPoint: SpendTooltipPayload | undefined = payload?.find(point => point.dataKey === 'ecpm');
  const isSpendPointEmpty = !spendPoint || spendPoint.value === undefined || spendPoint.value === null;

  if (!label || !payload || payload.length === 0 || isSpendPointEmpty) {
    return null;
  }

  const impressions = spendPoint.payload?.impressions;
  const vcr = spendPoint.payload?.vcr;
  const viewability = spendPoint.payload?.viewability;

  return (
    <Paper elevation={0} sx={{ py: 1, px: 2, border: `1px solid ${palette.divider}` }} data-test="spend-tooltip">
      <Typography data-test="spend-tooltip-date" variant="h3">
        {format(parseISO(label), 'MMM d, yyyy')}
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 1 }}>
        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, alignItems: 'center' }}>
          <Box sx={{ width: 12, height: 12, backgroundColor: palette.charts.blue, borderRadius: '50%' }} />
          <Typography data-test="spend-tooltip-spend" sx={{ display: 'flex' }}>
            {t('Spend')}{' '}
            <Typography
              variant="body1Bold"
              component={'span'}
              sx={{
                marginLeft: 1,
                display: 'flex',
              }}
            >
              ${spendPoint.value?.toLocaleString()}
            </Typography>
          </Typography>
        </Box>

        {ecpmPoint?.value !== undefined && ecpmPoint?.value !== null && (
          <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, alignItems: 'center' }}>
            <Box sx={{ width: 12, height: 12, backgroundColor: palette.charts.pink, borderRadius: '50%' }} />
            <Typography data-test="spend-tooltip-ecpm" sx={{ display: 'flex' }}>
              {t('CPM')}{' '}
              <Typography
                variant="body1Bold"
                component={'span'}
                sx={{
                  marginLeft: 1,
                  display: 'flex',
                }}
              >
                ${getFormattedAmountValue(Number(ecpmPoint.value))}
              </Typography>
            </Typography>
          </Box>
        )}
      </Box>

      <TooltipMetrics dataTestPrefix="spend-tooltip" impressions={impressions} viewability={viewability} vcr={vcr} />
    </Paper>
  );
};
